import { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { useAnalyticsClient } from './useAnalyticsClient';
import { useStickyState } from '../useStickyState';
import {
    Source,
    UserSession,
    WidgetType,
} from 'rwl-shared/dist/models/analytics';

export const SESSION_KEY = 'RWL:BI:SESSION';

interface AnalyticsSessionProviderProps {
    account?: string;
    source?: Source;
    widgetType?: WidgetType;
    integration?: string;
    ipAddress?: string;
}

interface AnalyticsSessionContextType {
    session: UserSession | null;
}

const AnalyticsSessionContext = createContext<
    AnalyticsSessionContextType | undefined
>(undefined);

export const AnalyticsSessionProvider = ({
    account,
    source,
    widgetType,
    integration,
    ipAddress,
    children,
}: PropsWithChildren<AnalyticsSessionProviderProps>): JSX.Element => {
    /**
     * Thanks to sticky state we get some perks:
     * - The session will be persisted across page reloads, but will be reset when the user leaves the site entirely
     * - The session will be shared across plugins on the same page
     */
    const [session, setSession] = useStickyState<UserSession | null>(
        null,
        SESSION_KEY,
        sessionStorage
    );
    const { client } = useAnalyticsClient();

    useEffect(() => {
        if (!client || !account || !source || !widgetType) {
            return;
        }

        if (!session) {
            setSession(
                client.createSession(
                    account,
                    source,
                    integration,
                    ipAddress,
                    widgetType
                )
            );
        }
    }, [client, session, account, source, integration, ipAddress]);

    return (
        <AnalyticsSessionContext.Provider value={{ session }}>
            {children}
        </AnalyticsSessionContext.Provider>
    );
};

export const useAnalyticsSession = (): AnalyticsSessionContextType => {
    const context = useContext(AnalyticsSessionContext);
    if (context === undefined) {
        throw new Error(
            'useAnalyticsSession must be used within an AnalyticsSessionProvider and also within an AnalyticsProvider'
        );
    }
    return context;
};
